import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Send, X } from "lucide-react";
import { formatApiError } from "@/context/AuthContext";

export const MAX_SUPPORT_IMAGE_BYTES = 1024 * 1024;

export default function SupportComposer({ onSend, placeholder = "Type a message…", inputTestId = "chat-input", sendTestId = "send-chat-btn" }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);
  const sending = useRef(false);
  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const select = (event) => {
    const selected = event.target.files?.[0]; event.target.value = ""; setError("");
    if (!selected) return;
    if (selected.size > MAX_SUPPORT_IMAGE_BYTES) { setError("Images must be 1 MB or smaller."); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(selected.type)) { setError("Choose a JPEG, PNG or WebP image."); return; }
    setFile(selected);
  };
  const send = async (event) => {
    event?.preventDefault();
    if (sending.current || (!text.trim() && !file)) return;
    sending.current = true; setBusy(true); setError("");
    try { await onSend(text.trim(), file); setText(""); setFile(null); }
    catch (e) { setError(e.response ? formatApiError(e.response.data?.detail) : "Message could not be sent. Please retry."); }
    finally { sending.current = false; setBusy(false); }
  };
  return <form onSubmit={send} className="p-3 border-t border-[#1E293B] space-y-2">
    {file && <div className="flex items-center gap-2 text-xs text-slate-300">
      {preview && <img src={preview} alt="Attachment preview" className="h-12 w-12 rounded-lg object-cover" />}
      <span className="truncate flex-1">{file.name}</span><button type="button" disabled={busy} onClick={() => setFile(null)} aria-label="Remove attached image"><X size={16} /></button>
    </div>}
    {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
    <div className="flex items-center gap-2">
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Choose support image" onChange={select} disabled={busy} className="hidden" />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} aria-label="Attach image (up to 1 MB)" title="Attach image · max 1 MB" className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[#1E2330] text-slate-300"><ImagePlus size={19} /></button>
      <input data-testid={inputTestId} value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} disabled={busy} placeholder={placeholder} className="min-w-0 flex-1 h-11 px-3 rounded-full bg-[#121721] border border-[#1E293B] text-white text-sm outline-none focus:border-[#00E676]" />
      <button data-testid={sendTestId} type="submit" disabled={busy || (!text.trim() && !file)} className="w-11 h-11 rounded-full bg-[#00E676] text-black flex items-center justify-center shrink-0 disabled:opacity-40">{busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}</button>
    </div>
    <p className="text-[10px] text-slate-500">JPEG, PNG or WebP · up to 1 MB per image</p>
  </form>;
}
